import React from 'react';
import { classNames } from '@vkontakte/vkjs';
import { EventEmitter, animate } from '@djs';
import { Touch } from '@vkontakte/vkui';
import { CustomTouchEventHandler } from '@dui';
import { getIntervalFunction } from './helpers';
import {
  cardSwipesContext,
  CardSwipesTransformListener,
  CardSwipesContext,
  CardSwipesDirectionListener,
} from './cardSwipesContext';
import { DeferredRender } from '../DeferredRender';
import { CardResolveDirection } from './types';
import styles from './CardSwipes.module.css';

export const COMPLETELY_SWIPE_RATIO_SHIFT = 0.25;

const FAST_SWIPE_MS_MAX = 200;
const FAST_SWIPE_RATIO_SHIFT = 0.1;
const SWIPE_FULL_ANIMATION_MS = 350;

export interface CardSwipesProps {
  className?: string;
  style?: React.CSSProperties;
  cardCurrentRootClassName?: string;
  cardNextRootClassName?: string;
  /**
   * Текущая карточка. Обязателен уникальный проп key.
   */
  card: React.ReactElement;
  /**
   * Следующие карточки, участвующие в свайпах. Обязателен уникальный проп key.
   * Если не передан - возможность свайпов отключается
   * Если передана одна - она будет следующей при свайпе влево или вправо
   */
  nextCard?: React.ReactElement;
  /**
   * Предыдущая карточка и направление её свайпа.
   * Необходимо использовать, если требуется анимация путем подмены пропсов.
   */
  historyTail?: { key: string; direction: CardResolveDirection };

  /**
   * Коллбек, вызывающийся после свайп-жеста, но перед анимацией резолва.
   * Если из коллбека вернуть false, то свайп отменится, резолва не будет, карточка вернется обратно.
   */
  onSwipeTouchEnd?(direction: CardResolveDirection): boolean;

  /**
   * Коллбек, вызывающийся после анимации резолва.
   * Т.е. карточка была полностью свайпнута, nextCard встала на передний план.
   * Если не передать, возможность свайпов с помощью жестов отключается.
   * Примечание: в обработчике onSwipeTransitionEnd нужно обновить пропсы так,
   * чтобы nextCard стал card, а также появился элемент в historyTail (по необходимости).
   */
  onResolveTransitionEnd?(direction: CardResolveDirection): void;

  /**
   * Рендерить следующую карточку сразу в фоне. По-умолчанию: true.
   */
  renderNextCardBeforeSwipe?: boolean;

  currentCardTestId?: string;
  nextCardTestId?: string;
}

interface CardSwipesState {
  card: React.ReactElement;
  cardRootClassName?: string;
  nextCard?: React.ReactElement;
  nextCardRootClassName?: string;
  historyTail?: { key: string; direction: CardResolveDirection };
  swipingDirection: CardResolveDirection | null;
  needTransition: {
    from: number;
    to: number;
    tmpCard?: React.ReactElement;
    tmpCardRootClassName?: string;
    tmpNextCard?: React.ReactElement;
    tmpNextCardRootClassName?: string;
  } | null;
  addWillChangeClass: boolean;
}

/**
 * Универсальный компонент для свайпов.
 * - Следуюшие карточки маунтятся только в процессе свайпов.
 * - Учтена проблема последовательного анмаунта и маунта карточки при изменении пропсов card и nextCard.
 * - Учтено анимирование при изменении пропсов извне (в т.ч. анимация возврата).
 */
export class CardSwipes extends React.Component<CardSwipesProps, CardSwipesState> {
  readonly state: Readonly<CardSwipesState> = {
    card: this.props.card,
    cardRootClassName: this.props.cardCurrentRootClassName,
    nextCard: this.props.nextCard,
    nextCardRootClassName: this.props.cardNextRootClassName,
    historyTail: this.props.historyTail,
    swipingDirection: null,
    needTransition: null,
    addWillChangeClass: false,
  };

  /**
   * Через getDerivedStateFromProps удается мемоизировать старые карточки для анимирования
   * их подмены через пропсы. Также getDerivedStateFromProps позволяет устанавливать правильный
   * swipingDirection без лишнего ререндера (в сравнении со случаем его переопределения
   * в componentDidUpdate или рядом с коллбеком onSwipe)
   */
  static getDerivedStateFromProps(props: CardSwipesProps, state: CardSwipesState): CardSwipesState | null {
    if (props.card.key === state.card.key && props.nextCard?.key === state.nextCard?.key) {
      return { ...state, card: props.card, nextCard: props.nextCard, historyTail: props.historyTail };
    }

    const newState: CardSwipesState = {
      ...state,
      card: props.card,
      cardRootClassName: props.cardCurrentRootClassName,
      nextCard: props.nextCard,
      nextCardRootClassName: props.cardNextRootClassName,
      historyTail: props.historyTail,
      swipingDirection: null,
      needTransition: null,
    };

    // Требуем анимацию замены пропсов извне (т.е. когда пропсы изменились без свайп-жеста).
    // Плюс учитываем случай, когда анимация не успела завершиться, а пропсы поменялись еще раз.
    if (state.swipingDirection === null || state.needTransition) {
      // Текущая карточка теперь попала в историю: свайп влево или вправо
      if (props.historyTail && props.historyTail.key === state.card.key) {
        return {
          ...newState,
          swipingDirection: null,
          needTransition: {
            from: 0,
            to: props.historyTail.direction,
            tmpCard: state.card,
            tmpCardRootClassName: state.cardRootClassName,
            tmpNextCard: state.nextCard,
            tmpNextCardRootClassName: state.nextCardRootClassName,
          },
        };
      }

      // Пришедшая карточка уже находилась в истории: возврат слева или справа
      if (state.historyTail && state.historyTail.key === props.card.key) {
        return {
          ...newState,
          swipingDirection: state.historyTail.direction,
          needTransition: {
            from: state.historyTail.direction,
            to: 0,
            tmpCard: props.card,
            tmpCardRootClassName: props.cardCurrentRootClassName,
            tmpNextCard: props.nextCard,
            tmpNextCardRootClassName: props.cardNextRootClassName,
          },
        };
      }
    }

    // В остальных случаях замена без анимации
    return newState;
  }

  componentDidUpdate(_: CardSwipesProps, prevState: CardSwipesState) {
    if (prevState.needTransition !== this.state.needTransition) {
      if (this.state.needTransition) {
        const { from, to } = this.state.needTransition;

        this.animateTransform(from, to, () => {
          this.setState({ swipingDirection: null, needTransition: null });
        });
      } else {
        this.cancelCurrentAnimateTransform?.();
      }
    }
  }

  componentRef = React.createRef<HTMLDivElement>();
  currentCardRef = React.createRef<HTMLDivElement>();
  nextCardRef = React.createRef<HTMLDivElement>();

  componentWidth = 300;

  willChangeTimeoutId: ReturnType<typeof setTimeout> | undefined;

  resizeObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          if (entries.length > 0) {
            const width = entries[0].contentRect.width;

            /** ResizeObserver нужен для одноразового чтения width без reflow,
             * после прочтения width он уже не нужен, отключаем чтобы предотвратить зацикливание
             * "ResizeObserver loop completed with undelivered notifications" */
            this.resizeObserver?.disconnect();

            /**
             * В iOS может прийти нулевая ширина и высота,
             * в этом случае фолбечимся на рефлоу, чтобы получить актуальные размеры.
             * Может быть тот же случай, что у IntersectionObserver в webkit
             * @see https://bugs.webkit.org/show_bug.cgi?id=196729
             */
            if (!width) {
              this.componentWidth = this.componentRef.current?.clientWidth || 0;
            } else {
              this.componentWidth = width;
            }
          }
        })
      : void 0;

  componentDidMount() {
    if (this.resizeObserver && this.componentRef.current) {
      this.resizeObserver.observe(this.componentRef.current);
    } else {
      // fallback with reflow
      this.componentWidth = this.componentRef.current?.clientWidth || 0;
    }

    this.willChangeTimeoutId = setTimeout(() => {
      // will-change ставить сразу не эффективно - в iOS видно белое моргание при переходе между панелями
      // в области ниже 100vh. В идеале will-change нужно ставить перед трансформацией,
      // но так как нам это никак не предугадать, мы его ставим хотя бы чуть позже рендера (это лечит проблему iOS).
      // Значение setTimeout было подобрано практическим путём.
      this.setState({ addWillChangeClass: true });
    }, 750);
  }

  componentWillUnmount() {
    this.willChangeTimeoutId && clearInterval(this.willChangeTimeoutId);
    this.resizeObserver?.disconnect();
  }

  /**
   * Доступность свайпов карточек с помощью жестов
   */
  get swipeGestureEnabled() {
    return Boolean(this.state.nextCard && !this.state.needTransition && this.props.onResolveTransitionEnd);
  }

  /**
   * Функции, помогающие в вычислениях трансформации карточек
   */

  private static readonly getNextCardScaleValue = getIntervalFunction(0.9, 1);

  private static readonly getNextCardBrightnessValue = getIntervalFunction(0.4, 1);

  /**
   * Подписчики на изменение трансформации карточки
   */

  private readonly emitter = new EventEmitter<{
    transformAnimationFrame: CardSwipesTransformListener;
    directionChanged: CardSwipesDirectionListener;
  }>();

  private readonly contextValue: CardSwipesContext = {
    addTransformAnimationFrameListener: (listener) => this.emitter.on('transformAnimationFrame', listener),
    removeTransformAnimationFrameListener: (listener) => this.emitter.off('transformAnimationFrame', listener),
    addDirectionListener: (listener) => this.emitter.on('directionChanged', listener),
    removeDirectionListener: (listener) => this.emitter.off('directionChanged', listener),
  };

  /**
   * Трансформирует положение карточек
   * @param shift сдвиг карточки в пикселях
   */
  setTransform = (shift: number) => {
    const swipingDirection: CardResolveDirection | null = (Math.sign(shift) as CardResolveDirection | 0) || null;

    if (this.state.swipingDirection !== swipingDirection) {
      this.setState({ swipingDirection });
      this.emitter.emit('directionChanged', { direction: swipingDirection });
    }

    const currentCardEl = this.currentCardRef.current;
    const nextCardEl = this.nextCardRef.current;

    const shiftRatio = this.componentWidth ? shift / this.componentWidth : 0;
    const absShiftRatio = Math.abs(shiftRatio);

    // Трансформация текущей карточки

    if (currentCardEl) {
      currentCardEl.style.filter = '';
      currentCardEl.style.transform = `translateX(${shift}px)`;
    }

    // Трансформация карточки на заднем плане

    if (nextCardEl) {
      if (absShiftRatio === 0) {
        nextCardEl.style.filter = '';
        nextCardEl.style.transform = '';
      } else {
        nextCardEl.style.filter = `brightness(${CardSwipes.getNextCardBrightnessValue(absShiftRatio)})`;
        nextCardEl.style.transform = `scale(${CardSwipes.getNextCardScaleValue(absShiftRatio)})`;
      }
    }

    // Отправка трансформации слушателям, подписанным через контекст
    this.emitter.emit('transformAnimationFrame', {
      shift,
      shiftRatio,
    });
  };

  cancelCurrentAnimateTransform?: VoidFunction;

  get transformAnimating() {
    return Boolean(this.cancelCurrentAnimateTransform);
  }

  /**
   * Запуск анимации изменения положения карточек
   * @param fromRatio начальное положение анимации [-1, 1]
   * @param toRatio конечное положение анимации [-1, 1]
   * @param onAnimationEnd коллбек завершения анимации
   */
  animateTransform = (fromRatio: number, toRatio: number, onAnimationEnd?: VoidFunction) => {
    this.cancelCurrentAnimateTransform?.();

    const ratioDiff = toRatio - fromRatio;
    const duration = Math.abs(ratioDiff) * SWIPE_FULL_ANIMATION_MS;

    const cancelAnimate = animate({
      duration,
      draw: (progress) => {
        this.setTransform((fromRatio + ratioDiff * progress) * this.componentWidth);
      },
      onAnimationEnd: () => {
        this.cancelCurrentAnimateTransform = void 0;
        onAnimationEnd?.();
      },
    });

    this.cancelCurrentAnimateTransform = () => {
      cancelAnimate();
      this.setTransform(0);
      this.cancelCurrentAnimateTransform = void 0;
    };
  };

  /**
   * Подписчики на свайп-жесты
   */

  startMoveXHandler: CustomTouchEventHandler = () => {
    if (this.transformAnimating) {
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.setTransform(0);
    });
  };

  rafId: number | undefined;

  moveXHandler: CustomTouchEventHandler = (event) => {
    if (this.transformAnimating || !this.swipeGestureEnabled) {
      return;
    }

    this.rafId && cancelAnimationFrame(this.rafId);

    this.rafId = requestAnimationFrame(() => {
      this.setTransform(event.shiftX);
    });

    // чинит появление браузерного PTR в iOS Chrome
    event.originalEvent.preventDefault();
  };

  endMoveXHandler: CustomTouchEventHandler = (event) => {
    if (this.transformAnimating || !this.swipeGestureEnabled) {
      return;
    }

    const ratioShift = event.shiftX / this.componentWidth;
    const swipeDirection = CardSwipes.detectSwipeCallbackDirection(event.shiftX / this.componentWidth, event.duration);

    if (!swipeDirection || (this.props.onSwipeTouchEnd && !this.props.onSwipeTouchEnd(swipeDirection))) {
      this.animateTransform(ratioShift, 0);
    } else {
      this.animateTransform(ratioShift, swipeDirection, () => this.props.onResolveTransitionEnd?.(swipeDirection));
    }
  };

  /**
   * Определяет полноценность жеста свайпа на основе сдвига и его длительности
   */
  private static detectSwipeCallbackDirection(ratioShift: number, duration: number) {
    const isFastSwipe = duration < FAST_SWIPE_MS_MAX && Math.abs(ratioShift) > FAST_SWIPE_RATIO_SHIFT;

    const isShortSwipeRight = isFastSwipe && ratioShift > 0;
    const isCompletelySwipeRight = ratioShift > COMPLETELY_SWIPE_RATIO_SHIFT;

    if (isShortSwipeRight || isCompletelySwipeRight) {
      return CardResolveDirection.Right;
    }

    const isShortSwipeLeft = isFastSwipe && ratioShift < 0;
    const isCompletelySwipeLeft = ratioShift < -COMPLETELY_SWIPE_RATIO_SHIFT;

    if (isShortSwipeLeft || isCompletelySwipeLeft) {
      return CardResolveDirection.Left;
    }

    return null;
  }

  /**
   * Рендер
   */
  render() {
    const { className, style, renderNextCardBeforeSwipe = true, currentCardTestId, nextCardTestId } = this.props;
    const { swipingDirection, needTransition, addWillChangeClass } = this.state;

    let card;
    let cardCurrentRootClassName;
    let nextCard;
    let cardNextRootClassName;

    if (needTransition) {
      card = needTransition.tmpCard;
      cardCurrentRootClassName = needTransition.tmpCardRootClassName;
      nextCard = needTransition.tmpNextCard;
      cardNextRootClassName = needTransition.tmpNextCardRootClassName;
    } else {
      card = this.state.card;
      cardCurrentRootClassName = this.state.cardRootClassName;
      nextCard = this.state.nextCard;
      cardNextRootClassName = this.state.nextCardRootClassName;
    }

    const isSwiping = swipingDirection !== null;
    const renderNext = renderNextCardBeforeSwipe || isSwiping;

    return (
      <cardSwipesContext.Provider value={this.contextValue}>
        <Touch
          style={style}
          className={classNames(styles.root, addWillChangeClass && styles.root__will_change, className)}
          onStartX={this.startMoveXHandler}
          onMoveX={this.moveXHandler}
          onEndX={this.endMoveXHandler}
          getRootRef={this.componentRef}
          data-swiping={isSwiping}
          data-testid="card-swipes"
          data-vkui-swipe-back={false}
        >
          {/* Передняя карточка должны быть выше в html dom, чтобы её ресурсы скачивались раньше */}
          <DeferredRender immediate key={card?.key}>
            <div
              data-testid={currentCardTestId}
              className={classNames(styles.card, cardCurrentRootClassName)}
              ref={this.currentCardRef}
            >
              {card}
            </div>
          </DeferredRender>
          {renderNext && nextCard && (
            <DeferredRender immediate={isSwiping} key={nextCard.key}>
              <div
                data-testid={nextCardTestId}
                ref={this.nextCardRef}
                className={classNames(
                  styles.card,
                  styles.card__next,
                  isSwiping && styles.card__next_visible,
                  cardNextRootClassName,
                )}
              >
                {nextCard}
              </div>
            </DeferredRender>
          )}
        </Touch>
      </cardSwipesContext.Provider>
    );
  }
}
